import React, {PureComponent, ReactNode} from 'react';
import {SimpleArtifactImage} from 'app/components/my_profile/artifacts/image';
import I18n from 'app/i18n';
import {stringTypeOfBonus} from 'app/utils/stringTypeOfBonus';
import SpriteIcon from 'app/utils/sprites/SpriteIcon';
import {lootboxPrizeTitle} from 'app/utils/lootboxPrizeTitle';
import {lootboxPrizeType, hideCalculatedPrice} from 'app/utils/lootboxPrizeType';
import {formatDropChance, formatLootboxMoney, formatPrizePrice, lootRarity} from 'app/utils/lootboxRarity';
import {fixed2int} from 'app/utils/fixed';
import LootInterface from 'app/interfaces/LootInterface';

const DIGITAL_ICON_SIZE = 85;
const FROZEN_BADGE_ICON_SIZE = 18;

interface Props {
    loot: LootInterface
}

export default class LootboxDetailsPrizeCard extends PureComponent<Props> {
    renderImage(): ReactNode {
        const {loot} = this.props;

        switch (lootboxPrizeType(loot)) {
        case 'points':
            return this.renderDigital(String(loot.points_amount), I18n.t('points', {count: loot.points_amount}));
        case 'money':
            return this.renderDigital(formatLootboxMoney(loot.money_amount));
        case 'frozen_money':
            return this.renderDigital(
                fixed2int(loot.frozen_money_amount || 0),
                I18n.t('frozen_balance'),
                'frozen'
            );
        case 'bonus':
            return this.renderDigital(
                formatLootboxMoney(loot.bonus_amount),
                stringTypeOfBonus(loot.bonus)
            );
        default: {
            const artifact = loot.recipe || loot.steam_skin;

            return artifact ? <SimpleArtifactImage artifact={artifact}/> : null;
        }
        }
    }

    renderDigital(amount: string, measure?: string, variant?: 'frozen'): ReactNode {
        const isFrozen = variant === 'frozen';
        const boxClass = `bonus-box lootbox-detail__prize-digital${isFrozen ? ' bonus-box--frozen' : ''}`;

        return <div className={boxClass}>
            <SpriteIcon
                name="#svg-icon-bonus"
                className={`bonus-icon${isFrozen ? ' bonus-icon--frozen' : ''}`}
                size={{height: DIGITAL_ICON_SIZE, width: DIGITAL_ICON_SIZE}}
                fill="#333437"
            />
            <div className="bonus-description">
                <span className="bonus-preview__amount">
                    {isFrozen && <SpriteIcon
                        name="#svg-balance-frozen"
                        className="bonus-preview__frozen-icon"
                        size={{height: FROZEN_BADGE_ICON_SIZE, width: FROZEN_BADGE_ICON_SIZE}}
                    />}{amount}
                </span>
                {measure && !isFrozen && <span className="bonus-preview__measure">{measure}</span>}
            </div>
        </div>;
    }

    render(): ReactNode {
        const {loot} = this.props;
        const rarity = lootRarity(loot);
        const chance = formatDropChance(loot.chance_percent);
        const price = hideCalculatedPrice(loot) ? null : formatPrizePrice(loot.calculated_price);

        const cardRarityClass = `lootbox-detail__prize-card${rarity === 'common' ? '' : ` lootbox-detail__prize-card--${rarity}`}`;

        return <div className={`lootbox-detail__prize lootbox-detail__prize--${rarity}`}>
            <div className={cardRarityClass}>
                <div className="lootbox-detail__prize-image">
                    {this.renderImage()}
                </div>
                {chance && <span className="lootbox-detail__prize-chance">{chance}</span>}
                {price && <span className="lootbox-detail__prize-price">{price}</span>}
            </div>
            <div className="lootbox-detail__prize-name">{lootboxPrizeTitle(loot)}</div>
        </div>;
    }
}
